import { z } from "zod";

/**
 * Roles a member of an organisation can hold. They mirror the hub's
 * organisation membership: the project roles, plus billing for subscription
 * management and owner for the organisation itself.
 */
export const organisationRoles = [
  "reader",
  "operator",
  "reviewer",
  "administrator",
  "billing",
  "owner",
] as const;
export const organisationRoleSchema = z.enum(organisationRoles);
export type OrganisationRole = z.infer<typeof organisationRoleSchema>;

/**
 * Only an administrator or the owner of an organisation may share its
 * connections with projects.
 */
export function canAdministerOrganisation(role: OrganisationRole | null): boolean {
  return role === "administrator" || role === "owner";
}

/** One organisation: the grouping of projects that share connections. */
export const organisationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1),
  createdAt: z.string().datetime(),
});
export type Organisation = z.infer<typeof organisationSchema>;

export const organisationListSchema = z.array(organisationSchema);
export type OrganisationList = z.infer<typeof organisationListSchema>;

/** One subject's membership of an organisation. */
export const organisationMemberSchema = z.object({
  organisationId: z.string().uuid(),
  subjectId: z.string().min(1),
  role: organisationRoleSchema,
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
});
export type OrganisationMember = z.infer<typeof organisationMemberSchema>;

export const organisationMemberListSchema = z.array(organisationMemberSchema);
export type OrganisationMemberList = z.infer<typeof organisationMemberListSchema>;

/**
 * One grant of an organisation connection to a project. The grant is what makes
 * the connection usable in that project and nowhere else.
 */
export const connectionGrantSchema = z.object({
  id: z.string().uuid(),
  organisationId: z.string().uuid(),
  connectionId: z.string().min(1),
  projectId: z.string().uuid(),
  scopes: z.array(z.string()),
  roleLimit: z.string().min(1),
  status: z.enum(["active", "revoked"]),
  createdAt: z.string().datetime(),
});
export type ConnectionGrant = z.infer<typeof connectionGrantSchema>;

export const connectionGrantListSchema = z.array(connectionGrantSchema);
export type ConnectionGrantList = z.infer<typeof connectionGrantListSchema>;
